import { useEffect, useState } from 'preact/hooks';

// Place search by name (Open-Meteo's free geocoder): for Location, and for the sky calendar's other places.

export interface GeoResult { name: string; admin1?: string; country?: string; country_code?: string; latitude: number; longitude: number; elevation?: number; timezone?: string }
export type SearchStatus = 'idle' | 'searching' | 'none' | 'failed';

// The house spellings of the two Holy Cities, for which the geocoder gives the English exonyms (only in Saudi Arabia:
// there is a Medina in Ohio and a Mecca in California).
const HOUSE: Record<string, string> = { Mecca: 'Makkah', Medina: 'Madinah', 'Mecca Region': 'Makkah Region', 'Medina Region': 'Madinah Region' };
const houseNames = (g: GeoResult): GeoResult =>
  g.country_code === 'SA' ? { ...g, name: HOUSE[g.name] ?? g.name, admin1: g.admin1 && (HOUSE[g.admin1] ?? g.admin1) } : g;

/** Places matching `q`, looked up a moment after typing stops. */
export function usePlaceSearch(q: string): { results: GeoResult[]; status: SearchStatus } {
  const [results, setResults] = useState<GeoResult[]>([]);
  const [status, setStatus] = useState<SearchStatus>('idle');
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setStatus('idle');
      return;
    }
    setStatus('searching');
    // A newer search cancels an older one, so a slow answer never replaces a newer one.
    const ctl = new AbortController();
    const id = setTimeout(async () => {
      try {
        const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q.trim())}&count=8&language=en&format=json`, { signal: ctl.signal });
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        if (ctl.signal.aborted) return;
        setResults(((j.results ?? []) as GeoResult[]).map(houseNames));
        setStatus(j.results?.length ? 'idle' : 'none');
      } catch {
        if (ctl.signal.aborted) return;
        setResults([]);
        setStatus('failed');
      }
    }, 280);
    return () => {
      clearTimeout(id);
      ctl.abort();
    };
  }, [q]);
  return { results, status };
}
